import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import {
    ConsigneeCheckinResponse,
    GetDiscrepanciesResponse,
    GetConsigneeAttachmentsResponse,
    LoadCarrier,
    SingleDeliveryNote,
    DiscrepancyResponse,
    ConsigneeAttachmentResponse,
} from 'api/models';

// ---------- Enriched delivery note ----------
export interface EnrichedDeliveryNote extends SingleDeliveryNote {
    loadCarrier: LoadCarrier[] | undefined;
    fullDiscrepancies: DiscrepancyResponse[] | undefined;
    attachments: ConsigneeAttachmentResponse[] | undefined;
    fullPalletTickets: ConsigneeAttachmentResponse[] | undefined;
    receipts: ConsigneeAttachmentResponse[] | undefined;
    discrepancyImages: ConsigneeAttachmentResponse[] | undefined;
}

// ---------- State interface ----------
export interface CheckinState {
    checkin: ConsigneeCheckinResponse | null;
    deliveryNotes: SingleDeliveryNote[];
    enrichedDeliveryNotes: EnrichedDeliveryNote[];
    orgSiteKey: string;
    discrepancyMap: Map<string, GetDiscrepanciesResponse>;
    attachmentMap: Map<string, GetConsigneeAttachmentsResponse>;
    palletTicketMap: Map<string, GetConsigneeAttachmentsResponse>;
    loadCarriersMap: Map<string, Array<LoadCarrier>>;
    receiptsMap: Map<string, GetConsigneeAttachmentsResponse>;
    discrepancyImagesMap: Map<string, GetConsigneeAttachmentsResponse>;
    driverSignaturePadInvalidate: boolean;
}

// ---------- Initial state ----------
export const initialCheckinState: CheckinState = {
    checkin: null,
    deliveryNotes: [],
    enrichedDeliveryNotes: [],
    orgSiteKey: '',
    discrepancyMap: new Map(),
    attachmentMap: new Map(),
    palletTicketMap: new Map(),
    loadCarriersMap: new Map(),
    receiptsMap: new Map(),
    discrepancyImagesMap: new Map(),
    driverSignaturePadInvalidate: false,
};

// ---------- NgRx Signal Store ----------
export const CheckinStore = signalStore(
    { providedIn: 'root' },
    withState<CheckinState>({ ...initialCheckinState }),
    withComputed(store => ({
        // All state fields are already exposed as signals by withState.
        // No additional derived computeds needed — the old getX selectors
        // were simple field accessors, which withState handles automatically.
    })),
    withMethods(store => ({
        setCheckin(checkin: ConsigneeCheckinResponse): void {
            patchState(store, { checkin });
        },
        setDeliveryNotes(notes: SingleDeliveryNote[]): void {
            patchState(store, { deliveryNotes: notes });
        },
        setEnrichedDeliveryNotes(notes: EnrichedDeliveryNote[]): void {
            patchState(store, { enrichedDeliveryNotes: notes });
        },
        setOrgSiteKey(key: string): void {
            patchState(store, { orgSiteKey: key });
        },
        setDiscrepancyMap(map: Map<string, GetDiscrepanciesResponse>): void {
            patchState(store, { discrepancyMap: new Map(map) });
        },
        setAttachmentMap(map: Map<string, GetConsigneeAttachmentsResponse>): void {
            patchState(store, { attachmentMap: new Map(map) });
        },
        setPalletTicketMap(map: Map<string, GetConsigneeAttachmentsResponse>): void {
            patchState(store, { palletTicketMap: new Map(map) });
        },
        setLoadCarriersMap(map: Map<string, Array<LoadCarrier>>): void {
            patchState(store, { loadCarriersMap: new Map(map) });
        },
        setReceiptsMap(map: Map<string, GetConsigneeAttachmentsResponse>): void {
            patchState(store, { receiptsMap: new Map(map) });
        },
        setDiscrepancyImagesMap(map: Map<string, GetConsigneeAttachmentsResponse>): void {
            patchState(store, { discrepancyImagesMap: new Map(map) });
        },
        invalidateDriverSignaturePad(): void {
            patchState(store, { driverSignaturePadInvalidate: !store.driverSignaturePadInvalidate() });
        },
        resetDateConsigneeSigned(): void {
            const checkin = store.checkin();
            if (!checkin) return;
            patchState(store, { checkin: { ...checkin, dateConsigneeSigned: 0 } });
        },
        clearCheckin(): void {
            patchState(store, {
                checkin: null,
                deliveryNotes: [],
                enrichedDeliveryNotes: [],
                orgSiteKey: '',
                discrepancyMap: new Map(),
                attachmentMap: new Map(),
                palletTicketMap: new Map(),
                loadCarriersMap: new Map(),
                receiptsMap: new Map(),
                discrepancyImagesMap: new Map(),
                driverSignaturePadInvalidate: false,
            });
        },
    }))
);

